<template>
  <Teleport to="body">
    <Transition name="file-explorer-operation-box">
      <div
        v-if="isShow"
        class="file-explorer-operation-box"
        :style="{
          width: `${contentWidth}px`,
        }"
        @mousedown.stop
        @click.stop
      >
        <!-- 操作栏 -->
        <ul
          ref="fileOperationContentRef"
          class="file-operations bottom"
        >
          <li
            v-for="item in menuOptions"
            :key="item.operation"
            :data-group="item.group || 'normal'"
          >
            <ElTooltip
              :disabled="isMobile || !item.labelI18nKey"
              :offset="24"
              v-bind="TOOLTIP_COMMON_PROPS"
            >
              <template #content>
                <div v-if="item.labelI18nKey">
                  <span>{{ $t(item.labelI18nKey) }}</span>
                  <span v-if="item.keyboard">{{ item.keyboard }}</span>
                </div>
              </template>
              <div @click.stop="() => handleSendContextMenuClickEvent(item.operation)">
                <component
                  :is="item.icon"
                  color="#ffffff"
                  hover-able
                  pointer
                />
              </div>
            </ElTooltip>
          </li>
        </ul>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, ref, toRaw } from '#imports'
import { ContextMenuEventName, operationsMeta } from '@/components/share/contextMenu.script'
import { Operation } from '@/components/share/contextMenu.type'
import { useElementSize, useEventBus } from '@vueuse/core'
import useShareFiles from '~/composables/useShareFiles'
import { useLayout } from '~/store/layout'
import { useShare } from '~/store/share'
import { TOOLTIP_COMMON_PROPS } from '~/types/constants'

const { isSingleFileMode = false } = defineProps<{
  isSingleFileMode?: boolean
}>()

const bus = useEventBus(ContextMenuEventName)
const layout = useLayout()
const shareStore = useShare()
const fileOperationContentRef = ref<HTMLElement>()
const isMobile = computed(() => layout.isMobile)
const files = useShareFiles()
const { width: contentWidth } = useElementSize(fileOperationContentRef, undefined, {
  box: 'border-box',
})

const showRestoreStatus = computed(() => {
  // 查询当前目录下已保存的文件
  const savedIds: string[] = shareStore.restoreCache.savedIds || []

  if (shareStore.selectedIds.length) {
    // 当前选中的文件中, 存在已保存的文件
    return shareStore.selectedIds.some(id => savedIds.includes(id))
  }

  const fileIds = files.value.map(item => item.id)
  return savedIds.some(id => fileIds.includes(id))
})

const isShow = computed(() => {
  // 展示条件
  // @1 存在已选中的文件
  // @2 存在已保存的文件
  // @3 存在选中并保存的文件
  // @4 移动端在选择模式时展示
  if ((isMobile.value && !shareStore.mobileSelectMode) || isSingleFileMode) return false
  return shareStore.selectedIds.length > 0 || showRestoreStatus.value
})

const menuOptions = computed(() =>
  operationsMeta.filter(el => el.limit(null, shareStore.selectedIds, showRestoreStatus.value) && !el.hideInBottom),
)

const handleSendContextMenuClickEvent = (operation: Operation) => {
  bus.emit(operation, toRaw(shareStore.selectedIds))
}
</script>

<style lang="less">
.file-explorer-operation-box-enter-active,
.file-explorer-operation-box-leave-active {
  transform: translate(-50%, 0) !important;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease !important;
}

.file-explorer-operation-box-enter-from,
.file-explorer-operation-box-leave-to {
  opacity: 0;
  transform: translate(-50%, 100px) !important;
}
</style>

<style lang="less" scoped>
.file-explorer-operation-box {
  max-width: calc(100vw - 40px);
  min-width: 120px;
  height: 48px;
  position: fixed;
  bottom: 108px;
  left: 50%;
  transform: translate(-50%, 0);

  border-radius: 24px;
  background: #3c3c3c;

  color: #fff;
  z-index: 9;
  overflow: auto hidden;
  transition: width 0.3s;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .file-operations {
    display: flex;
    gap: 24px;
    padding: 12px 24px;
    width: fit-content;
    margin: 0 auto;

    li {
      --color-primary: rgba(255, 255, 255, 0.8);

      &:not([data-group='normal']) + [data-group='normal'],
      &:not([data-group='status']) + [data-group='status'] {
        display: flex;
        align-items: center;

        &:before {
          content: '';
          display: block;
          background: fade(#e3e7eb, 40%);
          width: 1px;
          height: 18px;
          margin-right: 24px;
        }
      }
    }
  }
}
</style>
